import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ArrowLeft, ArrowRight, RotateCcw, Trophy } from "lucide-react";
import { computeNewRegime, computeOldRegime, formatINR } from "../../data/taxData";
import { ExpertHelpDialog } from "./ExpertHelpDialog";

const StepDots = ({ step, total }) => (
  <div className="flex items-center gap-2 mb-6">
    {Array.from({ length: total }).map((_, i) => (
      <div key={i} className={`h-2 rounded-full transition-all ${i <= step ? "bg-brand-orange w-8" : "bg-brand-border w-4"}`} />
    ))}
    <span className="text-sm text-brand-muted ml-2">Step {step + 1} of {total}</span>
  </div>
);

const NumInput = ({ id, label, value, onChange, hint }) => (
  <div>
    <Label htmlFor={id}>{label}</Label>
    <Input
      id={id}
      data-testid={id}
      type="number"
      min="0"
      placeholder="0"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 bg-white"
    />
    {hint && <p className="text-xs text-brand-muted mt-1">{hint}</p>}
  </div>
);

const PillButton = ({ active, onClick, children, ...rest }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
      active
        ? "bg-brand-orange text-white border-brand-orange"
        : "bg-white text-brand-navy border-brand-border hover:border-brand-orange"
    }`}
    {...rest}
  >
    {children}
  </button>
);

export const IncomeTaxCalculator = () => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    age: "<60",
    isSalaried: true,
    salary: "",
    otherIncome: "",
    d80c: "",
    d80d: "",
    nps: "",
    homeLoan: "",
    otherDed: "",
  });

  const set = (k) => (v) => setForm({ ...form, [k]: v });
  const num = (v) => parseFloat(v) || 0;
  const gross = num(form.salary) + num(form.otherIncome);
  const deductions =
    Math.min(num(form.d80c), 150000) +
    num(form.d80d) +
    Math.min(num(form.nps), 50000) +
    Math.min(num(form.homeLoan), 200000) +
    num(form.otherDed);

  const newR = computeNewRegime(gross, form.isSalaried);
  const oldR = computeOldRegime(gross, deductions, form.age, form.isSalaried);
  const savings = Math.abs(newR.total - oldR.total);
  const better = newR.total <= oldR.total ? "New Regime" : "Old Regime";

  const reset = () => {
    setStep(0);
    setForm({ age: "<60", isSalaried: true, salary: "", otherIncome: "", d80c: "", d80d: "", nps: "", homeLoan: "", otherDed: "" });
  };

  return (
    <div data-testid="income-tax-calculator">
      <StepDots step={step} total={4} />

      {step === 0 && (
        <Card className="bg-white border border-brand-border"><CardContent className="p-6 space-y-5">
          <h3 className="text-lg font-semibold text-brand-navy">Basic Details — FY 2025-26 (AY 2026-27)</h3>
          <div>
            <Label>Age Category</Label>
            <div className="flex gap-2 mt-2 flex-wrap">
              {[["<60", "Below 60"], ["60-80", "60 to 80 (Senior)"], ["80+", "Above 80 (Super Senior)"]].map(([v, l]) => (
                <PillButton
                  key={v}
                  data-testid={`age-${v === "<60" ? "below60" : v === "60-80" ? "senior" : "supersenior"}`}
                  onClick={() => set("age")(v)}
                  active={form.age === v}
                >{l}</PillButton>
              ))}
            </div>
          </div>
          <div>
            <Label>Are you a salaried employee / pensioner?</Label>
            <div className="flex gap-2 mt-2">
              {[[true, "Yes (Salaried)"], [false, "No (Business/Other)"]].map(([v, l]) => (
                <PillButton
                  key={l}
                  data-testid={`salaried-${v ? "yes" : "no"}`}
                  onClick={() => set("isSalaried")(v)}
                  active={form.isSalaried === v}
                >{l}</PillButton>
              ))}
            </div>
            <p className="text-xs text-brand-muted mt-2">Salaried taxpayers get standard deduction: ₹75,000 (new regime) / ₹50,000 (old regime)</p>
          </div>
        </CardContent></Card>
      )}

      {step === 1 && (
        <Card className="bg-white border border-brand-border"><CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold text-brand-navy">Income Details</h3>
          <NumInput id="itc-salary" label={form.isSalaried ? "Gross Annual Salary (₹)" : "Annual Business / Professional Income (₹)"} value={form.salary} onChange={set("salary")} />
          <NumInput id="itc-other-income" label="Other Income (₹)" value={form.otherIncome} onChange={set("otherIncome")} hint="Interest, rental income, capital gains taxed at slab rates, etc." />
        </CardContent></Card>
      )}

      {step === 2 && (
        <Card className="bg-white border border-brand-border"><CardContent className="p-6 space-y-4">
          <h3 className="text-lg font-semibold text-brand-navy">Deductions & Exemptions <span className="text-sm font-normal text-brand-muted">(applicable in Old Regime only)</span></h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <NumInput id="itc-80c" label="Section 80C (₹)" value={form.d80c} onChange={set("d80c")} hint="PPF, ELSS, LIC, EPF — max ₹1,50,000" />
            <NumInput id="itc-80d" label="Section 80D — Health Insurance (₹)" value={form.d80d} onChange={set("d80d")} />
            <NumInput id="itc-nps" label="NPS 80CCD(1B) (₹)" value={form.nps} onChange={set("nps")} hint="Max ₹50,000" />
            <NumInput id="itc-homeloan" label="Home Loan Interest 24(b) (₹)" value={form.homeLoan} onChange={set("homeLoan")} hint="Max ₹2,00,000" />
          </div>
          <NumInput id="itc-other-ded" label="Other Deductions — HRA, 80G, 80E etc. (₹)" value={form.otherDed} onChange={set("otherDed")} />
        </CardContent></Card>
      )}

      {step === 3 && (
        <div className="space-y-5">
          <div className="bg-brand-navy rounded-xl p-6 text-white flex items-center gap-4" data-testid="itc-recommendation">
            <Trophy className="h-10 w-10 text-brand-orange shrink-0" />
            <div>
              <p className="text-white/70 text-sm">Recommended for you</p>
              <p className="text-2xl font-bold">{better}</p>
              {savings > 0 && <p className="text-white/80 text-sm">You save {formatINR(savings)} compared to the other regime</p>}
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { name: "New Regime", r: newR, note: "Default regime — standard deduction ₹75,000, rebate up to ₹12L income" },
              { name: "Old Regime", r: oldR, note: "With your deductions of " + formatINR(deductions) },
            ].map(({ name, r, note }) => (
              <Card key={name} className={`bg-white border ${better === name ? "border-brand-orange border-2" : "border-brand-border"}`} data-testid={`itc-result-${name === "New Regime" ? "new" : "old"}`}>
                <CardContent className="p-5">
                  <h4 className="font-semibold text-brand-navy mb-1">{name}</h4>
                  <p className="text-xs text-brand-muted mb-4">{note}</p>
                  <div className="space-y-2 text-sm text-brand-navy">
                    <div className="flex justify-between"><span className="text-brand-muted">Gross Income</span><span>{formatINR(gross)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-muted">Taxable Income</span><span>{formatINR(r.taxable)}</span></div>
                    <div className="flex justify-between"><span className="text-brand-muted">Tax as per slabs</span><span>{formatINR(r.baseTax)}</span></div>
                    {r.rebate > 0 && <div className="flex justify-between text-emerald-600"><span>Rebate u/s 87A</span><span>- {formatINR(r.rebate)}</span></div>}
                    {r.surcharge > 0 && <div className="flex justify-between"><span className="text-brand-muted">Surcharge</span><span>{formatINR(r.surcharge)}</span></div>}
                    <div className="flex justify-between"><span className="text-brand-muted">Health & Edu Cess (4%)</span><span>{formatINR(r.cess)}</span></div>
                    <div className="flex justify-between pt-2 border-t border-brand-border font-bold text-base"><span>Total Tax</span><span className="text-brand-orange">{formatINR(r.total)}</span></div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-between mt-6">
        <Button variant="outline" data-testid="itc-back-btn" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="border-brand-border text-brand-navy hover:bg-brand-grey">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        {step < 3 ? (
          <Button data-testid="itc-next-btn" className="bg-brand-orange hover:bg-brand-orange-dark text-white" onClick={() => setStep(step + 1)}>
            {step === 2 ? "Calculate Tax" : "Next"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <div className="flex gap-3">
            <Button variant="outline" data-testid="itc-reset-btn" onClick={reset} className="border-brand-border text-brand-navy hover:bg-brand-grey">
              <RotateCcw className="h-4 w-4 mr-2" /> Start Over
            </Button>
            <ExpertHelpDialog
              service="Income Tax"
              prefillDetails={`[Income Tax Calculator - FY 2025-26]\nGross Income: ${formatINR(gross)}\nDeductions claimed (old regime): ${formatINR(deductions)}\nNew Regime Tax: ${formatINR(newR.total)}\nOld Regime Tax: ${formatINR(oldR.total)}\nRecommended: ${better}\n\nI would like expert help with my income tax filing.`}
            />
          </div>
        )}
      </div>
    </div>
  );
};
